import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Users, 
  Ticket, 
  DollarSign, 
  TrendingUp, 
  Copy, 
  Check,
  RefreshCw,
  Shield
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [codes, setCodes] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [codeCount, setCodeCount] = useState(10);
  const [copiedCode, setCopiedCode] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    // التحقق من صلاحيات الإدارة
    if (user?.role !== "admin") {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    
    fetchStats();
    fetchCodes();
    fetchSubscriptions();
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
        toast.error("غير مصرح - صلاحيات الإدارة مطلوبة");
      } else {
        toast.error("خطأ في تحميل الإحصائيات");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/codes/list`);
      setCodes(response.data.codes);
    } catch (error) {
      toast.error("خطأ في تحميل الأكواد");
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const response = await axios.get(`${API}/admin/subscriptions/list`);
      setSubscriptions(response.data.subscriptions);
    } catch (error) {
      toast.error("خطأ في تحميل المشتركين");
    }
  };

  const handleGenerateCodes = async () => {
    if (codeCount < 1 || codeCount > 1000) {
      toast.error("الرجاء إدخال عدد بين 1 و 1000");
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/admin/codes/generate`, {
        count: parseInt(codeCount)
      });
      toast.success(response.data.message);
      fetchCodes();
      fetchStats();
    } catch (error) {
      toast.error("خطأ في توليد الأكواد");
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success("تم نسخ الكود");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("ar-IQ", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat("ar-IQ").format(num);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mx-auto"></div>
          <p className="mt-4">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-md mx-auto text-center mt-20">
            <div className="bg-red-50 border border-red-200 rounded-lg p-8">
              <Shield className="w-16 h-16 text-red-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-red-900 mb-2">وصول غير مصرح</h2>
              <p className="text-red-700 mb-4">
                هذه الصفحة مخصصة للمسؤولين فقط
              </p>
              <Button onClick={() => window.location.href = "/dashboard"}>
                العودة للصفحة الرئيسية
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">لوحة تحكم الإدارة</h1>
          <p className="text-lg text-slate-600">إدارة الاشتراكات والأكواد</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">إجمالي المستخدمين</CardTitle>
              <Users className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatNumber(stats?.users.total || 0)}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">المشتركين النشطين</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatNumber(stats?.subscriptions.active || 0)}</div>
              <p className="text-xs text-slate-500 mt-1">
                من أصل {formatNumber(stats?.subscriptions.total || 0)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">الأكواد المتاحة</CardTitle>
              <Ticket className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatNumber(stats?.codes.unused || 0)}</div>
              <p className="text-xs text-slate-500 mt-1">
                مستخدم: {formatNumber(stats?.codes.used || 0)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">إجمالي الإيرادات</CardTitle>
              <DollarSign className="h-4 w-4 text-amber-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatNumber(stats?.revenue.total_iqd || 0)}</div>
              <p className="text-xs text-slate-500 mt-1">دينار عراقي</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Card>
          <CardContent className="pt-6">
            <Tabs defaultValue="generate" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="generate">توليد أكواد</TabsTrigger>
                <TabsTrigger value="codes">الأكواد</TabsTrigger>
                <TabsTrigger value="subscriptions">المشتركين</TabsTrigger>
              </TabsList>

              {/* Generate Codes Tab */}
              <TabsContent value="generate" className="space-y-4">
                <div className="max-w-md mx-auto text-center py-8">
                  <Ticket className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold mb-2">توليد أكواد تفعيل جديدة</h3>
                  <p className="text-slate-600 mb-6">
                    كل كود صالح لاشتراك سنة كاملة (365 يوم) بسعر 15,000 دينار عراقي
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="code-count">عدد الأكواد</Label>
                      <Input
                        id="code-count"
                        type="number"
                        min="1"
                        max="1000"
                        value={codeCount}
                        onChange={(e) => setCodeCount(e.target.value)}
                        className="text-center text-lg"
                      />
                    </div>
                    
                    <Button 
                      onClick={handleGenerateCodes} 
                      disabled={generating}
                      className="w-full"
                      size="lg"
                    >
                      {generating ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          جاري التوليد...
                        </>
                      ) : (
                        <>
                          <Ticket className="w-4 h-4 mr-2" />
                          توليد {codeCount} كود
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* Codes List Tab */}
              <TabsContent value="codes" className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">قائمة الأكواد</h3>
                  <Button variant="outline" size="sm" onClick={fetchCodes}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    تحديث
                  </Button>
                </div>
                
                <div className="max-h-[500px] overflow-y-auto space-y-2">
                  {codes.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">لا توجد أكواد</p>
                  ) : (
                    codes.map((code) => (
                      <div
                        key={code.code}
                        className={`p-4 rounded-lg border ${
                          code.is_used 
                            ? "bg-slate-50 border-slate-200" 
                            : "bg-green-50 border-green-200"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <code className="text-lg font-mono font-bold">
                                {code.code}
                              </code>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyToClipboard(code.code)}
                              >
                                {copiedCode === code.code ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                            <div className="mt-2 text-sm text-slate-600">
                              {code.is_used ? (
                                <span className="text-red-600">
                                  ✓ مستخدم - {formatDate(code.used_at)}
                                </span>
                              ) : (
                                <span className="text-green-600">
                                  ✓ متاح للاستخدام
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-semibold">{formatNumber(code.price_iqd)} د.ع</p>
                            <p className="text-xs text-slate-500">{code.duration_days} يوم</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* Subscriptions List Tab */}
              <TabsContent value="subscriptions" className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">قائمة المشتركين</h3>
                  <Button variant="outline" size="sm" onClick={fetchSubscriptions}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    تحديث
                  </Button>
                </div>
                
                <div className="max-h-[500px] overflow-y-auto space-y-2">
                  {subscriptions.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">لا يوجد مشتركين</p>
                  ) : (
                    subscriptions.map((sub) => (
                      <div key={sub.id} className="p-4 rounded-lg border bg-white">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold">{sub.user_name}</h4>
                            <p className="text-sm text-slate-600">{sub.user_email}</p>
                            <div className="mt-2 text-xs text-slate-500">
                              <p>الكود: <code className="font-mono">{sub.activation_code}</code></p>
                              <p>تاريخ التفعيل: {formatDate(sub.activated_at)}</p>
                              <p>ينتهي في: {formatDate(sub.expires_at)}</p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            sub.is_active 
                              ? "bg-green-100 text-green-700" 
                              : "bg-red-100 text-red-700"
                          }`}>
                            {sub.is_active ? "نشط" : "منتهي"}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
